import React, { useState, useEffect } from 'react';
import axios from 'axios';


const API_URL = process.env.REACT_APP_API_URL?.replace('/api', '');

function WIMPayment() {

  // Data states
  const [violations, setViolations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedViolation, setSelectedViolation] = useState(null);

  // Payment states
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [cardId, setCardId] = useState('');
  const [balance, setBalance] = useState(null);

  // QR Payment states
  const [qrImage, setQrImage] = useState(null);
  const [qrTransactionData, setQrTransactionData] = useState(null);
  const [qrPaymentStatus, setQrPaymentStatus] = useState(null);
  const [qrStatusInterval, setQrStatusInterval] = useState(null);

  // UI states
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState('unpaid');

  // Load violations from backend
  const loadViolations = React.useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');

      if (!token) {
        setMessage({ type: 'error', text: 'Please login first' });
        setLoading(false);
        return;
      }

      const response = await axios.get(
        `${API_URL}/api/wim/violations?status=${filterStatus}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      let data = response.data || [];
      console.log('Load Violatios - Filter:', filterStatus, 'Count:', data.length);

      // Client-side filtering safeguard
      if (filterStatus) {
        data = data.filter(v => {
          const s = v.status ? v.status.toString().toLowerCase().trim() : '';
          const f = filterStatus.toLowerCase().trim();
          const match = s === f;
          if (!match && f === 'paid') console.log('Filtered out:', v.license_plate, v.status);
          return match;
        });
      }

      console.log('Filtered Count:', data.length);
      setViolations(data);
      setMessage({ type: '', text: '' });
    } catch (error) {
      console.error('Load violations error:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to load violations'
      });
      setViolations([]);
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  // Load violations on mount and when filter changes
  useEffect(() => {
    loadViolations();
  }, [loadViolations]);

  // Check balance
  const checkBalance = React.useCallback(async (card, deviceType) => {
    if (!card) return;

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/manual-payment/check-balance`,
        { cardId: card, deviceType },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setBalance(response.data.balance);
      }
    } catch (error) {
      console.error('Balance check error:', error);
      setBalance(null);
    }
  }, []);

  // Check balance when card ID changes
  useEffect(() => {
    if (cardId && cardId.length >= 8 && (paymentMethod === 'card' || paymentMethod === 'obu')) {
      checkBalance(cardId, paymentMethod === 'card' ? 'mifare' : 'obu');
    } else {
      setBalance(null);
    }
  }, [cardId, paymentMethod, checkBalance]);

  // Cleanup QR interval on unmount
  useEffect(() => {
    return () => {
      if (qrStatusInterval) {
        clearInterval(qrStatusInterval);
      }
    };
  }, [qrStatusInterval]);

  // Open payment modal
  const openPaymentModal = (violation) => {
    setSelectedViolation(violation);
    setShowPaymentModal(true);
    setPaymentMethod('cash');
    setCardId('');
    setBalance(null);
    setQrImage(null);
    setQrTransactionData(null);
    setQrPaymentStatus(null);
    setMessage({ type: '', text: '' });
  };

  // Close payment modal
  const closePaymentModal = () => {
    setShowPaymentModal(false);
    setSelectedViolation(null);
    setCardId('');
    setBalance(null);
    setQrImage(null);
    setQrTransactionData(null);
    setQrPaymentStatus(null);
    if (qrStatusInterval) {
      clearInterval(qrStatusInterval);
      setQrStatusInterval(null);
    }
    setMessage({ type: '', text: '' });
  };

  // Generate QR code for payment
  const generateQRCode = async () => {
    if (!selectedViolation) return;
    const amount = parseFloat(selectedViolation.fine_amount || 0);
    if (amount <= 0) {
      setMessage({ type: 'error', text: 'Invalid fine amount' });
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/ldb/generate-qr`,
        {
          amount: amount,
          description: `WIM Fine - ${selectedViolation.license_plate || 'Unknown'} - Overweight ${selectedViolation.overweight_kg || selectedViolation.event_overweight} kg`,
          mobile_num: '2097285066'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setQrImage(response.data.qrCodeImage);
        setQrTransactionData({
          transactionId: response.data.transactionId,
          reference2: response.data.reference2,
          amount: response.data.amount
        });
        setQrPaymentStatus('pending');

        // Start monitoring payment status
        startQrPaymentMonitoring(response.data.transactionId, response.data.reference2);

        setMessage({ type: 'success', text: 'QR Code generated! Please scan to pay.' });
      }
    } catch (error) {
      console.error('QR generation error:', error);
      setMessage({ type: 'error', text: 'Failed to generate QR code: ' + (error.response?.data?.error || error.message) });
    } finally {
      setLoading(false);
    }
  };

  // Monitor QR payment status
  const startQrPaymentMonitoring = (transactionId, reference2) => {
    if (qrStatusInterval) {
      clearInterval(qrStatusInterval);
    }

    const interval = setInterval(async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${API_URL}/api/ldb/query-transaction/${transactionId}?reference2=${reference2}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data.success && response.data.isPaid) {
          clearInterval(interval);
          setQrStatusInterval(null);
          setQrPaymentStatus('paid');
          setMessage({ type: 'success', text: '✅ QR Payment received! Processing...' });

          // Auto-process payment
          await processQrPayment(transactionId, reference2);
        }
      } catch (error) {
        console.error('QR status check error:', error);
      }
    }, 3000);

    setQrStatusInterval(interval);
  };

  // Process QR payment after confirmed
  const processQrPayment = async (qrTxnId, ref2) => {
    if (!selectedViolation) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/wim/violations/${selectedViolation.id}/pay`,
        {
          method: 'qr',
          qrTransactionId: qrTxnId,
          qrReference2: ref2
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setMessage({ type: 'success', text: 'ຊຳລະຄ່າປັບ QR ສຳເລັດ!' });

        // Generate PDF receipt
        await openPdfReceipt(response.data.violationId || selectedViolation.id);

        setTimeout(async () => {
          closePaymentModal();
          await loadViolations();
        }, 2000);
      }
    } catch (error) {
      console.error('QR Payment error:', error);
      setMessage({ type: 'error', text: error.response?.data?.message || 'QR Payment failed' });
    } finally {
      setLoading(false);
    }
  };

  // Open PDF receipt - uses <a> click to avoid popup blocker
  const openPdfReceipt = async (violationId) => {
    try {
      const token = localStorage.getItem('token');
      // Small delay to ensure DB transaction is committed
      await new Promise(resolve => setTimeout(resolve, 500));

      const pdfResponse = await axios.get(
        `${API_URL}/api/wim/receipt-pdf/${violationId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      const blob = new Blob([pdfResponse.data], { type: 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);

      // Use <a> tag click instead of window.open to avoid popup blocker
      const link = document.createElement('a');
      link.href = blobUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 10000);
      console.log('📄 WIM receipt PDF opened for violation', violationId);
    } catch (pdfError) {
      console.error('PDF generation error:', pdfError);
      setMessage({ type: 'warning', text: 'ຊຳລະສຳເລັດ ແຕ່ບໍ່ສາມາດສ້າງໃບບິນ PDF ໄດ້' });
    }
  };

  // Process payment (cash, mifare, obu)
  const processPayment = async (e) => {
    e.preventDefault();

    if (!selectedViolation) return;

    // For QR, payment is handled by processQrPayment
    if (paymentMethod === 'qr') {
      if (qrPaymentStatus !== 'paid') {
        setMessage({ type: 'warning', text: 'Please wait for QR payment to be confirmed first' });
      }
      return;
    }

    // Validate card/device for mifare/obu
    if ((paymentMethod === 'card' || paymentMethod === 'obu') && !cardId) {
      setMessage({ type: 'error', text: paymentMethod === 'card' ? 'ກະລຸນາໃສ່ Card UID' : 'ກະລຸນາໃສ່ Device ID' });
      return;
    }

    const amount = parseFloat(selectedViolation.fine_amount || 0);
    const currentBalance = parseFloat(balance);

    if ((paymentMethod === 'card' || paymentMethod === 'obu') && balance !== null && currentBalance < amount) {
      setMessage({ type: 'error', text: 'ຍອດເງິນບໍ່ພຽງພໍ' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('token');
      const payload = {
        method: paymentMethod === 'card' ? 'mifare' : paymentMethod,
        card_uid: paymentMethod === 'card' ? cardId : undefined,
        device_id: paymentMethod === 'obu' ? cardId : undefined
      };

      const response = await axios.post(
        `${API_URL}/api/wim/violations/${selectedViolation.id}/pay`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setMessage({ type: 'success', text: '✅ ຊຳລະຄ່າປັບສຳເລັດ! ກຳລັງສ້າງໃບບິນ...' });

        // Generate PDF receipt
        const receiptViolationId = response.data.violationId || selectedViolation.id;
        await openPdfReceipt(receiptViolationId);

        // Wait 2 seconds to show success message, then close and refresh
        setTimeout(async () => {
          closePaymentModal();
          await loadViolations();
        }, 2000);
      }
    } catch (error) {
      console.error('Payment error:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Payment failed'
      });
    } finally {
      setLoading(false);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format number with commas
  const formatNumber = (num) => {
    if (num == null) return '-';
    return parseFloat(num).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 p-4">
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="bg-white dark:bg-gray-800 rounded-t-xl shadow-lg p-6 border-b-4 border-purple-500">
          <h1 className="text-3xl font-bold text-gray-800 dark:text-white flex items-center gap-3">
            <span className="text-4xl">⚖️</span>
            WIM Payment - ຊຳລະຄ່າປັບ WIM
          </h1>
          <p className="text-gray-600 dark:text-gray-300 mt-2">
            Weigh-In-Motion Violation Payment System - ລະບົບຊຳລະຄ່າປັບນ້ຳໜັກເກີນ
          </p>
        </div>

        {/* Filter Buttons */}
        <div className="bg-white dark:bg-gray-800 shadow-lg p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex gap-3">
            <button
              onClick={() => setFilterStatus('unpaid')}
              className={`px-6 py-2 rounded-lg font-semibold transition ${filterStatus === 'unpaid'
                ? 'bg-red-500 text-white shadow-lg'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300'
                }`}
            >
              <span className="mr-2">⚠️</span>
              Unpaid - ຍັງບໍ່ຈ່າຍ
            </button>
            <button
              onClick={() => setFilterStatus('paid')}
              className={`px-6 py-2 rounded-lg font-semibold transition ${filterStatus === 'paid'
                ? 'bg-green-500 text-white shadow-lg'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300'
                }`}
            >
              <span className="mr-2">✅</span>
              Paid - ຈ່າຍແລ້ວ
            </button>
            <button
              onClick={() => setFilterStatus('')}
              className={`px-6 py-2 rounded-lg font-semibold transition ${filterStatus === ''
                ? 'bg-blue-500 text-white shadow-lg'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300'
                }`}
            >
              <span className="mr-2">📋</span>
              All - ທັງໝົດ
            </button>
            <button
              onClick={loadViolations}
              className="ml-auto px-6 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg font-semibold transition shadow"
            >
              <span className="mr-2">🔄</span>
              Refresh
            </button>
          </div>
        </div>

        {/* Alert Messages */}
        {message.text && !showPaymentModal && (
          <div className={`bg-white dark:bg-gray-800 shadow-lg p-4 border-l-4 ${message.type === 'success' ? 'border-green-500 bg-green-50 dark:bg-green-900' :
            message.type === 'error' ? 'border-red-500 bg-red-50 dark:bg-red-900' :
              'border-blue-500 bg-blue-50 dark:bg-blue-900'
            }`}>
            <p className={`text-sm ${message.type === 'success' ? 'text-green-800 dark:text-green-200' :
              message.type === 'error' ? 'text-red-800 dark:text-red-200' :
                'text-blue-800 dark:text-blue-200'
              }`}>
              {message.text}
            </p>
          </div>
        )}

        {/* Violations Table */}
        <div className="bg-white dark:bg-gray-800 rounded-b-xl shadow-lg overflow-hidden">
          {loading && violations.length === 0 ? (
            <div className="flex items-center justify-center p-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
              <span className="ml-4 text-gray-600 dark:text-gray-300">Loading violations...</span>
            </div>
          ) : violations.length === 0 ? (
            <div className="text-center p-12">
              <span className="text-6xl">📭</span>
              <p className="text-gray-500 dark:text-gray-400 mt-4 text-lg">
                No violations found - ບໍ່ມີລາຍການປັບ
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-purple-100 to-indigo-100 dark:from-purple-900 dark:to-indigo-900">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                      Event Time<br />ເວລາ
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                      License Plate<br />ປ້າຍທະບຽນ
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                      Gross Weight<br />ນ້ຳໜັກລວມ (kg)
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                      Legal Weight<br />ນ້ຳໜັກຕາມກົດໝາຍ (kg)
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                      Overweight<br />ນ້ຳໜັກເກີນ (kg)
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                      Fine Amount<br />ຄ່າປັບ (₭)
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                      Status<br />ສະຖານະ
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                      Action<br />ດຳເນີນການ
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {violations.map((violation, idx) => {
                    if (idx < 2) console.log(`Row ${idx} render:`, violation.license_plate, '| status:', violation.status, '| trimmed:', (violation.status || '').toString().toLowerCase().trim());
                    return (
                      <tr
                        key={violation.id}
                        className={`hover:bg-gray-50 dark:hover:bg-gray-700 transition ${idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-750'
                          }`}
                      >
                        <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                          {formatDate(violation.event_time)}
                        </td>
                        <td className="px-4 py-3 text-sm font-bold text-gray-900 dark:text-white">
                          {violation.license_plate || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 text-right">
                          {formatNumber(violation.gross_weight_kg)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 text-right">
                          {formatNumber(violation.legal_weight_kg)}
                        </td>
                        <td className="px-4 py-3 text-sm font-bold text-red-600 dark:text-red-400 text-right">
                          {formatNumber(violation.overweight_kg || violation.event_overweight)}
                        </td>
                        <td className="px-4 py-3 text-sm font-bold text-purple-600 dark:text-purple-400 text-right">
                          {formatNumber(violation.fine_amount)}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {['paid', 'success'].includes((violation.status || '').toString().toLowerCase().trim()) ? (
                            <span className="px-3 py-1 bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded-full text-xs font-bold">
                              ✅ Paid
                            </span>
                          ) : (
                            <span className="px-3 py-1 bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 rounded-full text-xs font-bold">
                              ⚠️ Unpaid
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {(!['paid', 'success'].includes((violation.status || '').toString().toLowerCase().trim())) && (
                            <button
                              onClick={() => openPaymentModal(violation)}
                              className="px-4 py-2 bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white text-xs font-bold rounded-lg shadow transition transform hover:scale-105"
                            >
                              💳 ດຳເນີນການ
                            </button>
                          )}
                          {['paid', 'success'].includes((violation.status || '').toString().toLowerCase().trim()) && (
                            <span className="text-gray-400 text-xs">
                              {violation.paid_method ? `${violation.paid_method.toUpperCase()} • ` : ''}{formatDate(violation.paid_at)}
                            </span>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ═══════════════════ Payment Modal ═══════════════════ */}
      {showPaymentModal && selectedViolation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">

            {/* Modal Header */}
            <div className="bg-gradient-to-r from-purple-500 to-indigo-600 p-6 rounded-t-xl">
              <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                <span className="text-3xl">💳</span>
                ຊຳລະຄ່າປັບ - Process Payment
              </h2>
            </div>

            {/* Modal Body */}
            <div className="p-6">

              {/* Alert Messages */}
              {message.text && (
                <div className={`mb-4 p-4 rounded-lg border-l-4 ${message.type === 'success' ? 'bg-green-50 border-green-500 text-green-800 dark:bg-green-900 dark:text-green-200' :
                  message.type === 'error' ? 'bg-red-50 border-red-500 text-red-800 dark:bg-red-900 dark:text-red-200' :
                    message.type === 'warning' ? 'bg-yellow-50 border-yellow-500 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                      'bg-blue-50 border-blue-500 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                  }`}>
                  {message.text}
                </div>
              )}

              {/* Violation Details */}
              <div className="bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-900 dark:to-orange-900 rounded-lg p-6 mb-6 border-2 border-red-200 dark:border-red-700">
                <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-4">
                  ລາຍລະອຽດການລະເມີດ - Violation Details
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">ເວລາ:</span>
                    <p className="font-bold text-gray-900 dark:text-white">{formatDate(selectedViolation.event_time)}</p>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">ປ້າຍທະບຽນ:</span>
                    <p className="font-bold text-gray-900 dark:text-white text-lg">{selectedViolation.license_plate || '-'}</p>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">ນ້ຳໜັກລວມ:</span>
                    <p className="font-bold text-gray-900 dark:text-white">{formatNumber(selectedViolation.gross_weight_kg)} kg</p>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">ນ້ຳໜັກຕາມກົດໝາຍ:</span>
                    <p className="font-bold text-gray-900 dark:text-white">{formatNumber(selectedViolation.legal_weight_kg)} kg</p>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">ນ້ຳໜັກເກີນ:</span>
                    <p className="font-bold text-red-600 dark:text-red-400 text-xl">
                      {formatNumber(selectedViolation.overweight_kg || selectedViolation.event_overweight)} kg
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">ຈຳນວນເພົາ:</span>
                    <p className="font-bold text-gray-900 dark:text-white">{selectedViolation.axle_count || '-'} ເພົາ</p>
                  </div>
                  <div className="col-span-2 pt-4 border-t-2 border-red-300 dark:border-red-700">
                    <span className="text-gray-700 dark:text-gray-300 font-semibold">ຄ່າປັບລວມ - Total Fine:</span>
                    <p className="font-bold text-purple-600 dark:text-purple-400 text-3xl">
                      {formatNumber(selectedViolation.fine_amount)} ₭
                    </p>
                  </div>
                  {selectedViolation.tier_name && (
                    <div className="col-span-2">
                      <span className="text-gray-600 dark:text-gray-400">ອັດຕາປັບ:</span>
                      <p className="font-bold text-gray-900 dark:text-white">{selectedViolation.tier_name}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Payment Form */}
              <form onSubmit={processPayment}>

                {/* Payment Method Selection - 2x2 Grid */}
                <div className="mb-6">
                  <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-3">
                    ວິທີຊຳລະເງິນ - Payment Method <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {/* Cash */}
                    <label className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition ${paymentMethod === 'cash'
                      ? 'bg-green-100 border-green-500 dark:bg-green-900 dark:border-green-400 ring-2 ring-green-300'
                      : 'bg-white border-gray-300 dark:bg-gray-700 dark:border-gray-600 hover:border-green-400'
                      }`}>
                      <input
                        type="radio"
                        value="cash"
                        checked={paymentMethod === 'cash'}
                        onChange={(e) => { setPaymentMethod(e.target.value); setCardId(''); setBalance(null); setQrImage(null); setQrPaymentStatus(null); }}
                        className="w-5 h-5"
                      />
                      <span className="text-2xl">💵</span>
                      <span className="font-semibold text-gray-800 dark:text-white">Cash - ເງິນສົດ</span>
                    </label>

                    {/* QR */}
                    <label className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition ${paymentMethod === 'qr'
                      ? 'bg-indigo-100 border-indigo-500 dark:bg-indigo-900 dark:border-indigo-400 ring-2 ring-indigo-300'
                      : 'bg-white border-gray-300 dark:bg-gray-700 dark:border-gray-600 hover:border-indigo-400'
                      }`}>
                      <input
                        type="radio"
                        value="qr"
                        checked={paymentMethod === 'qr'}
                        onChange={(e) => { setPaymentMethod(e.target.value); setCardId(''); setBalance(null); }}
                        className="w-5 h-5"
                      />
                      <span className="text-2xl">📱</span>
                      <span className="font-semibold text-gray-800 dark:text-white">QR (LDB)</span>
                    </label>

                    {/* Mifare */}
                    <label className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition ${paymentMethod === 'card'
                      ? 'bg-blue-100 border-blue-500 dark:bg-blue-900 dark:border-blue-400 ring-2 ring-blue-300'
                      : 'bg-white border-gray-300 dark:bg-gray-700 dark:border-gray-600 hover:border-blue-400'
                      }`}>
                      <input
                        type="radio"
                        value="card"
                        checked={paymentMethod === 'card'}
                        onChange={(e) => { setPaymentMethod(e.target.value); setCardId(''); setBalance(null); setQrImage(null); setQrPaymentStatus(null); }}
                        className="w-5 h-5"
                      />
                      <span className="text-2xl">💳</span>
                      <span className="font-semibold text-gray-800 dark:text-white">MIFARE Card</span>
                    </label>

                    {/* OBU */}
                    <label className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition ${paymentMethod === 'obu'
                      ? 'bg-purple-100 border-purple-500 dark:bg-purple-900 dark:border-purple-400 ring-2 ring-purple-300'
                      : 'bg-white border-gray-300 dark:bg-gray-700 dark:border-gray-600 hover:border-purple-400'
                      }`}>
                      <input
                        type="radio"
                        value="obu"
                        checked={paymentMethod === 'obu'}
                        onChange={(e) => { setPaymentMethod(e.target.value); setCardId(''); setBalance(null); setQrImage(null); setQrPaymentStatus(null); }}
                        className="w-5 h-5"
                      />
                      <span className="text-2xl">📡</span>
                      <span className="font-semibold text-gray-800 dark:text-white">OBU Device</span>
                    </label>
                  </div>
                </div>

                {/* ═══ QR Payment Section ═══ */}
                {paymentMethod === 'qr' && (
                  <div className="mb-6">
                    {!qrImage && !loading && (
                      <div className="bg-indigo-50 dark:bg-indigo-900/30 border-2 border-indigo-300 dark:border-indigo-700 rounded-lg p-6 text-center">
                        <p className="text-sm text-indigo-800 dark:text-indigo-200 mb-3">
                          ກົດປຸ່ມເພື່ອສ້າງ QR Code ສຳລັບການຊຳລະ
                        </p>
                        <button
                          type="button"
                          onClick={generateQRCode}
                          disabled={loading}
                          className="px-8 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition font-bold text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          📱 Generate QR Code
                        </button>
                      </div>
                    )}

                    {qrImage && (
                      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900 dark:to-purple-900 rounded-lg p-6 border-2 border-indigo-300 dark:border-indigo-600">
                        <h3 className="text-lg font-bold text-center mb-4 text-indigo-700 dark:text-indigo-300">
                          📱 Scan QR Code to Pay
                        </h3>

                        <div className="flex justify-center mb-4">
                          <img
                            src={qrImage}
                            alt="QR Code"
                            className="w-48 h-48 border-4 border-white rounded-lg shadow-lg"
                          />
                        </div>

                        <div className="text-center space-y-2">
                          <p className="text-3xl font-bold text-indigo-700 dark:text-indigo-300">
                            {formatNumber(selectedViolation.fine_amount)} ₭
                          </p>

                          {qrPaymentStatus === 'pending' && (
                            <div className="bg-yellow-100 dark:bg-yellow-900/50 rounded-lg p-3 mt-3">
                              <p className="text-sm text-yellow-800 dark:text-yellow-200 flex items-center justify-center gap-2">
                                <span className="animate-pulse text-xl">⏳</span>
                                <span className="font-semibold">ກຳລັງລໍຖ້າການຊຳລະ...</span>
                              </p>
                              <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                                Checking payment status every 3 seconds
                              </p>
                            </div>
                          )}

                          {qrPaymentStatus === 'paid' && (
                            <div className="bg-green-100 dark:bg-green-900/50 rounded-lg p-3 mt-3">
                              <p className="text-sm text-green-800 dark:text-green-200 flex items-center justify-center gap-2">
                                <span className="text-xl">✅</span>
                                <span className="font-semibold">ຊຳລະແລ້ວ! ກຳລັງປະມວນຜົນ...</span>
                              </p>
                            </div>
                          )}

                          <div className="flex gap-3 justify-center mt-4">
                            <button
                              type="button"
                              onClick={() => {
                                setQrImage(null);
                                setQrTransactionData(null);
                                setQrPaymentStatus(null);
                                if (qrStatusInterval) {
                                  clearInterval(qrStatusInterval);
                                  setQrStatusInterval(null);
                                }
                              }}
                              className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition"
                            >
                              🔄 Regenerate
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* ═══ Mifare / OBU — Card/Device ID ═══ */}
                {(paymentMethod === 'card' || paymentMethod === 'obu') && (
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {paymentMethod === 'card' ? '💳 Card UID' : '📡 Device ID'} <span className="text-red-500">*</span>
                    </label>

                    {/* Show linked device info if available */}
                    {paymentMethod === 'card' && selectedViolation.mifare_card && (
                      <div className="mb-2 p-2 bg-blue-50 dark:bg-blue-900 rounded border border-blue-200 dark:border-blue-700">
                        <p className="text-xs text-blue-700 dark:text-blue-300">
                          ℹ️ Linked Card: <span className="font-mono font-bold">{selectedViolation.mifare_card}</span>
                          <button
                            type="button"
                            onClick={() => setCardId(selectedViolation.mifare_card)}
                            className="ml-2 text-blue-600 hover:text-blue-800 underline text-xs"
                          >
                            Use This
                          </button>
                        </p>
                      </div>
                    )}
                    {paymentMethod === 'obu' && selectedViolation.obu_device && (
                      <div className="mb-2 p-2 bg-purple-50 dark:bg-purple-900 rounded border border-purple-200 dark:border-purple-700">
                        <p className="text-xs text-purple-700 dark:text-purple-300">
                          ℹ️ Linked Device: <span className="font-mono font-bold">{selectedViolation.obu_device}</span>
                          <button
                            type="button"
                            onClick={() => setCardId(selectedViolation.obu_device)}
                            className="ml-2 text-purple-600 hover:text-purple-800 underline text-xs"
                          >
                            Use This
                          </button>
                        </p>
                      </div>
                    )}

                    <input
                      type="text"
                      value={cardId}
                      onChange={(e) => setCardId(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
                      className="w-full p-4 text-lg border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white dark:border-gray-600 font-mono"
                      placeholder={paymentMethod === 'card' ? 'Enter Card UID' : 'Enter OBU Device ID'}
                      required
                    />

                    {/* Balance display */}
                    {balance !== null && (
                      <div className={`mt-3 p-4 rounded-lg border-2 ${parseFloat(balance) >= parseFloat(selectedViolation.fine_amount)
                        ? 'bg-green-50 border-green-500 dark:bg-green-900 dark:border-green-500'
                        : 'bg-red-50 border-red-500 dark:bg-red-900 dark:border-red-500'
                        }`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                              ຍອດເງິນຄົງເຫຼືອ:
                            </p>
                            <p className={`text-2xl font-bold ${parseFloat(balance) >= parseFloat(selectedViolation.fine_amount)
                              ? 'text-green-600 dark:text-green-400'
                              : 'text-red-600 dark:text-red-400'
                              }`}>
                              {formatNumber(balance)} ₭
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                              ຕ້ອງຈ່າຍ:
                            </p>
                            <p className="text-xl font-bold text-gray-900 dark:text-white">
                              {formatNumber(selectedViolation.fine_amount)} ₭
                            </p>
                          </div>
                        </div>
                        {parseFloat(balance) < parseFloat(selectedViolation.fine_amount) && (
                          <p className="mt-2 text-sm font-bold text-red-700 dark:text-red-300">
                            ⚠️ ຍອດເງິນບໍ່ພຽງພໍ
                          </p>
                        )}
                        {parseFloat(balance) >= parseFloat(selectedViolation.fine_amount) && (
                          <p className="mt-2 text-sm font-bold text-green-700 dark:text-green-300">
                            ✅ ຍອດເງິນພຽງພໍ
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* ═══ Cash confirmation info ═══ */}
                {paymentMethod === 'cash' && (
                  <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/30 border-2 border-green-300 dark:border-green-700 rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl">💵</span>
                      <div>
                        <p className="font-bold text-green-800 dark:text-green-200">ຊຳລະດ້ວຍເງິນສົດ - Cash Payment</p>
                        <p className="text-sm text-green-700 dark:text-green-300">
                          ຈຳນວນເງິນ: <span className="font-bold text-lg">{formatNumber(selectedViolation.fine_amount)} ₭</span>
                        </p>
                        <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                          ກົດ "ຢືນຢັນການຊຳລະ" ເພື່ອບັນທຶກການຊຳລະເງິນສົດ
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3">
                  {paymentMethod !== 'qr' && (
                    <button
                      type="submit"
                      disabled={loading || (paymentMethod !== 'cash' && balance !== null && parseFloat(balance) < parseFloat(selectedViolation.fine_amount))}
                      className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-bold py-4 px-6 rounded-xl shadow-lg hover:shadow-xl transition transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none text-lg"
                    >
                      {loading ? (
                        <span className="flex items-center justify-center gap-2">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                          Processing...
                        </span>
                      ) : (
                        <span className="flex items-center justify-center gap-2">
                          <span className="text-2xl">✅</span>
                          ຢືນຢັນການຊຳລະ - Confirm Payment
                        </span>
                      )}
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={closePaymentModal}
                    disabled={loading}
                    className="px-6 py-4 bg-gray-500 hover:bg-gray-600 text-white font-bold rounded-xl shadow transition disabled:opacity-50"
                  >
                    ❌ ຍົກເລີກ
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default WIMPayment;
